import React from 'react';
import { SectionShell } from './FormControls';
import {
  ULTRASONIC_ROW_KEYS,
  ULTRASONIC_GRID_LABELS,
  ULTRASONIC_ROW_LABELS,
  ULTRASONIC_SHELL_KEYS,
  ULTRASONIC_SHELL_LABELS,
  ULTRASONIC_SHELL_PANELS,
  ULTRASONIC_END_PANEL_KEYS,
  ULTRASONIC_ADDITIONAL_PANEL_KEYS,
  ULTRASONIC_ADDITIONAL_GRID_LABELS,
  ULTRASONIC_SHARED_ROW_CAPACITY,
  END_READING_POSITIONS,
  SHELL_COL_COUNT,
} from '../fieldNames';

const isControlTaken = (value) => Number.isFinite(parseFloat(String(value ?? '').trim()));

// Single small numeric cell.
const Cell = ({ value, onChange, disabled }) => (
  <input
    type="text"
    inputMode="decimal"
    value={value || ''}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    className={`w-14 px-1 py-1 text-center text-xs bg-pvs-bg/40 border border-pvs-border rounded text-white focus:outline-none focus:border-pvs-accent ${
      disabled ? 'opacity-40 cursor-not-allowed' : ''
    }`}
  />
);

const HeaderCell = ({ children, accent }) => (
  <div
    className={`text-[10px] font-semibold text-center uppercase tracking-wide ${
      accent ? 'text-pvs-accent' : 'text-pvs-text-muted'
    }`}
  >
    {children}
  </div>
);

// One main-shell panel grid: 3 rows of 12 SHELL cells.  ENDR / ENDL columns
// are not shown here -- they live on the end-cap panels (panel1, panel3).
const PanelGrid = ({ panelKey, title, data, updateCell, disabled }) => (
  <div className={`pvs-glass border border-pvs-border rounded-lg p-4 ${disabled ? 'opacity-60' : ''}`}>
    <h4 className="text-xs font-semibold text-white mb-3 uppercase tracking-wide">
      {title}
    </h4>
    <div className="overflow-x-auto -mx-2 px-2">
      <div className="inline-block min-w-full">
        {/* Column headers: row label + 1..12 */}
        <div
          className="grid gap-1 mb-2"
          style={{ gridTemplateColumns: `64px repeat(${SHELL_COL_COUNT}, minmax(56px, 1fr))` }}
        >
          <div />
          {Array.from({ length: SHELL_COL_COUNT }, (_, i) => (
            <HeaderCell key={`h-${i}`}>{i + 1}</HeaderCell>
          ))}
        </div>

        {ULTRASONIC_ROW_KEYS.map((rowKey) => {
          const row = data?.[rowKey] || { shell: [] };
          return (
            <div
              key={rowKey}
              className="grid gap-1 mb-1 items-center"
              style={{ gridTemplateColumns: `64px repeat(${SHELL_COL_COUNT}, minmax(56px, 1fr))` }}
            >
              <div className="text-[11px] font-semibold text-white pr-2 text-right">
                {ULTRASONIC_ROW_LABELS[rowKey]}
              </div>
              {Array.from({ length: SHELL_COL_COUNT }, (_, i) => (
                <Cell
                  key={`${rowKey}-${i}`}
                  value={row.shell?.[i]}
                  disabled={disabled}
                  onChange={(v) => updateCell(panelKey, rowKey, i, v)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

// Circular end-cap panel.  Six readings arranged around a circle to mirror
// where the inspector physically takes them on the dished end:
//   R1 / R2 / R3 -- right half (30 deg / 90 deg / 20 deg from BDC past TDC)
//   R4 / R5 / R6 -- left  half (mirror image)
// The SVG is purely decorative -- inputs are HTML siblings positioned with
// the same trig so they remain native (focus rings, mobile keyboards, etc.).
const CIRCLE_BOX = 360;          // outer container (px)
const CIRCLE_CENTER = CIRCLE_BOX / 2;
const CIRCLE_RADIUS = 130;       // ring radius for input centres
const CIRCLE_VISUAL_RADIUS = 110; // dashed circle drawn on the SVG
const TICK_LEN = 8;              // little tick marks at TDC / BDC

const polarToXY = (angleDeg, radius) => {
  // Angle measured CW from TDC (12 o'clock), in degrees.
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CIRCLE_CENTER + radius * Math.sin(rad),
    y: CIRCLE_CENTER - radius * Math.cos(rad),
  };
};

// Centre data-plate dimensions (Panel 1 only -- the data-plate end of the
// vessel).  Sized so the spokes from the readings die into the plate edges
// rather than crossing the rectangle.
const DATA_PLATE_W = 70;
const DATA_PLATE_H = 40;
const DATA_PLATE_RX = 4;

const CircularEndPanel = ({ panelKey, title, readings, updateEndReading, showDataPlate, disabled }) => (
  <div className={`pvs-glass border border-pvs-border rounded-lg p-4 ${disabled ? 'opacity-60' : ''}`}>
    <h4 className="text-xs font-semibold text-white mb-3 uppercase tracking-wide text-center">
      {title}
    </h4>
    <p className="text-[11px] text-pvs-text-muted text-center mb-3">
      View looking onto the end cap. Take readings clockwise from TDC.
    </p>

    <div
      className="relative mx-auto"
      style={{ width: CIRCLE_BOX, height: CIRCLE_BOX }}
    >
      {/* Decorative end-cap diagram. */}
      <svg
        viewBox={`0 0 ${CIRCLE_BOX} ${CIRCLE_BOX}`}
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
      >
        {/* Outer circle = end cap perimeter. */}
        <circle
          cx={CIRCLE_CENTER}
          cy={CIRCLE_CENTER}
          r={CIRCLE_VISUAL_RADIUS}
          fill="none"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="1.5"
          strokeDasharray="4 4"
        />
        {showDataPlate ? (
          // Rounded data-plate at centre (data-plate end of the vessel).
          <g>
            <rect
              x={CIRCLE_CENTER - DATA_PLATE_W / 2}
              y={CIRCLE_CENTER - DATA_PLATE_H / 2}
              width={DATA_PLATE_W}
              height={DATA_PLATE_H}
              rx={DATA_PLATE_RX}
              ry={DATA_PLATE_RX}
              fill="rgba(255,255,255,0.04)"
              stroke="rgba(255,255,255,0.45)"
              strokeWidth="1.25"
            />
            <text
              x={CIRCLE_CENTER}
              y={CIRCLE_CENTER + 3}
              textAnchor="middle"
              className="fill-pvs-text-muted"
              style={{ fontSize: 9, letterSpacing: 1.5 }}
            >DATA PLATE</text>
          </g>
        ) : (
          // Centre cross marker (non-data-plate end).
          <g>
            <line
              x1={CIRCLE_CENTER - 6} y1={CIRCLE_CENTER}
              x2={CIRCLE_CENTER + 6} y2={CIRCLE_CENTER}
              stroke="rgba(255,255,255,0.25)" strokeWidth="1"
            />
            <line
              x1={CIRCLE_CENTER} y1={CIRCLE_CENTER - 6}
              x2={CIRCLE_CENTER} y2={CIRCLE_CENTER + 6}
              stroke="rgba(255,255,255,0.25)" strokeWidth="1"
            />
          </g>
        )}
        {/* TDC / BDC ticks. */}
        <line
          x1={CIRCLE_CENTER} y1={CIRCLE_CENTER - CIRCLE_VISUAL_RADIUS - TICK_LEN}
          x2={CIRCLE_CENTER} y2={CIRCLE_CENTER - CIRCLE_VISUAL_RADIUS}
          stroke="rgba(255,255,255,0.45)" strokeWidth="1.5"
        />
        <line
          x1={CIRCLE_CENTER} y1={CIRCLE_CENTER + CIRCLE_VISUAL_RADIUS}
          x2={CIRCLE_CENTER} y2={CIRCLE_CENTER + CIRCLE_VISUAL_RADIUS + TICK_LEN}
          stroke="rgba(255,255,255,0.45)" strokeWidth="1.5"
        />
        <text
          x={CIRCLE_CENTER}
          y={CIRCLE_CENTER - CIRCLE_VISUAL_RADIUS - TICK_LEN - 4}
          textAnchor="middle"
          className="fill-pvs-text-muted"
          style={{ fontSize: 10, letterSpacing: 1 }}
        >TDC</text>
        <text
          x={CIRCLE_CENTER}
          y={CIRCLE_CENTER + CIRCLE_VISUAL_RADIUS + TICK_LEN + 12}
          textAnchor="middle"
          className="fill-pvs-text-muted"
          style={{ fontSize: 10, letterSpacing: 1 }}
        >BDC</text>

        {/* Spokes to each reading position.  When the data plate is shown
            the inner end of each spoke is pushed out to the plate edge so
            the line dies into the rectangle rather than crossing it. */}
        {END_READING_POSITIONS.map(({ idx, angle }) => {
          let innerR = 18;
          if (showDataPlate) {
            const rad = (angle * Math.PI) / 180;
            const dx = Math.abs(Math.sin(rad));
            const dy = Math.abs(Math.cos(rad));
            const tX = dx > 1e-6 ? (DATA_PLATE_W / 2 + 3) / dx : Infinity;
            const tY = dy > 1e-6 ? (DATA_PLATE_H / 2 + 3) / dy : Infinity;
            innerR = Math.min(tX, tY);
          }
          const inner = polarToXY(angle, innerR);
          const outer = polarToXY(angle, CIRCLE_VISUAL_RADIUS);
          return (
            <line
              key={`spoke-${idx}`}
              x1={inner.x} y1={inner.y}
              x2={outer.x} y2={outer.y}
              stroke="rgba(255,255,255,0.12)" strokeWidth="1"
            />
          );
        })}
      </svg>

      {/* HTML input clusters positioned around the ring. */}
      {END_READING_POSITIONS.map(({ idx, angle, label }) => {
        const { x, y } = polarToXY(angle, CIRCLE_RADIUS);
        return (
          <div
            key={idx}
            className="absolute flex flex-col items-center"
            style={{
              left: x,
              top: y,
              transform: 'translate(-50%, -50%)',
              width: 84,
            }}
          >
            <span className="text-[10px] font-semibold uppercase tracking-wide text-pvs-accent leading-none mb-0.5">
              R{idx + 1}
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={readings?.[idx] || ''}
              disabled={disabled}
              onChange={(e) => updateEndReading(panelKey, idx, e.target.value)}
              placeholder="mm"
              className={`w-16 px-1 py-1 text-center text-xs bg-pvs-bg/70 border border-pvs-border rounded text-white focus:outline-none focus:border-pvs-accent ${
                disabled ? 'opacity-40 cursor-not-allowed' : ''
              }`}
            />
            <span className="text-[9px] text-pvs-text-muted leading-tight mt-0.5 text-center whitespace-nowrap">
              {label}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

// A labelled Control reading input.
const ControlInput = ({ label = 'Control', value, onChange, required }) => {
  const empty = !String(value || '').trim();
  return (
    <label className="flex items-center gap-2 text-xs text-pvs-text-muted shrink-0">
      <span className="uppercase tracking-wide">
        {label}
        {required ? <span className="text-amber-400 ml-0.5">*</span> : null}
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="mm"
        aria-required={required || undefined}
        className={`w-20 px-2 py-1 text-center text-xs bg-pvs-bg/40 border rounded text-white focus:outline-none focus:border-pvs-accent ${
          required && empty ? 'border-amber-500/70' : 'border-pvs-border'
        }`}
      />
      <span className="text-pvs-text-dim">mm</span>
    </label>
  );
};

const LockedPanelNotice = () => (
  <p className="text-xs text-amber-300 bg-amber-900/20 border border-amber-500/30 rounded px-3 py-2">
    Enter the Control reading above before taking panel readings.
  </p>
);

// Group of one or more panels sharing a single Control reading.
const ShellGroup = ({
  shellKey,
  ultrasonic,
  updateCell,
  updateControl,
  updateEndReading,
  additionalShell,
  toggleAdditionalShell,
}) => {
  const panelKeys = ULTRASONIC_SHELL_PANELS[shellKey] || [];
  const controlValue = ultrasonic?.controls?.[shellKey] || '';
  const isMainShell = shellKey === 'mainShell';
  const panelsLocked = !isControlTaken(controlValue);
  const additionalControlTaken = isControlTaken(ultrasonic?.controls?.mainShell2);
  return (
    <div className="md:col-span-2 pvs-glass border border-pvs-accent/40 rounded-lg p-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h3 className="text-sm font-semibold text-white">
          {ULTRASONIC_SHELL_LABELS[shellKey]}
        </h3>
        <ControlInput required value={controlValue} onChange={(v) => updateControl(shellKey, v)} />
      </div>
      {panelsLocked ? <LockedPanelNotice /> : null}
      {panelKeys.map((panelKey) => {
        const isEndPanel = ULTRASONIC_END_PANEL_KEYS.includes(panelKey);
        const panelData = ultrasonic?.[panelKey];
        const title = ULTRASONIC_GRID_LABELS[panelKey];
        return isEndPanel ? (
          <CircularEndPanel
            key={panelKey}
            panelKey={panelKey}
            title={title}
            readings={panelData?.readings}
            updateEndReading={updateEndReading}
            showDataPlate={panelKey === 'panel1'}
            disabled={panelsLocked}
          />
        ) : (
          <PanelGrid
            key={panelKey}
            panelKey={panelKey}
            title={title}
            data={panelData}
            updateCell={updateCell}
            disabled={panelsLocked}
          />
        );
      })}

      {isMainShell && !additionalShell && (
        <button
          type="button"
          onClick={() => toggleAdditionalShell(true)}
          className="w-full px-4 py-2.5 rounded-lg border border-dashed border-pvs-accent/60 text-pvs-accent text-sm font-semibold hover:bg-pvs-accent/10"
        >
          + Add another main shell
        </button>
      )}

      {isMainShell && additionalShell && (
        <div className="border-t border-pvs-accent/30 pt-4 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h3 className="text-sm font-semibold text-white">Main Shell 2 (Additional)</h3>
            <div className="flex items-center gap-3">
              <ControlInput
                required
                value={ultrasonic?.controls?.mainShell2}
                onChange={(v) => updateControl('mainShell2', v)}
              />
              <button
                type="button"
                onClick={() => toggleAdditionalShell(false)}
                className="text-xs px-3 py-1.5 border border-pvs-border rounded text-white hover:bg-white/5"
              >
                Remove
              </button>
            </div>
          </div>
          <p className="text-[11px] text-pvs-text-muted">
            On the certificate these readings continue on the same rows as Panels 2 and 4,
            separated by one N/A column. Combined readings per row must not exceed
            {' '}{ULTRASONIC_SHARED_ROW_CAPACITY} cells (front: Panels 2+5, back: Panels 4+6).
          </p>
          {additionalControlTaken ? null : <LockedPanelNotice />}
          {ULTRASONIC_ADDITIONAL_PANEL_KEYS.map((panelKey) => (
            <PanelGrid
              key={panelKey}
              panelKey={panelKey}
              title={ULTRASONIC_ADDITIONAL_GRID_LABELS[panelKey]}
              data={ultrasonic?.[panelKey]}
              updateCell={updateCell}
              disabled={!additionalControlTaken}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const UltrasonicSection = ({
  data,
  updateCell,
  updateControl,
  updateEndReading,
  toggleAdditionalShell,
}) => (
  <SectionShell
    title="Ultrasonic Thickness Check"
    description="Readings in mm. Each shell has one Control reading (mandatory) shared by every panel attached to it (Panels 2 and 4 are both the Main Shell, opposite sides). Panel readings stay locked until that Control is entered. End-shell readings are taken in a circle around the dished end. Every reading on every panel must sit within ±10% of its shell's control."
  >
    <div className="md:col-span-2 space-y-6">
      {ULTRASONIC_SHELL_KEYS.map((shellKey) => (
        <ShellGroup
          key={shellKey}
          shellKey={shellKey}
          ultrasonic={data}
          updateCell={updateCell}
          updateControl={updateControl}
          updateEndReading={updateEndReading}
          additionalShell={!!data?.additionalShell}
          toggleAdditionalShell={toggleAdditionalShell}
        />
      ))}
    </div>
  </SectionShell>
);

export default UltrasonicSection;
